// ===========================================
// SERVICE: Calendario (CORE)
// ===========================================
/**
 * Servicio de gestión de calendario, vencimientos fiscales y eventos.
 * Requiere cliente autenticado de Supabase como primer parámetro.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { format, startOfMonth, endOfMonth, addDays, parseISO } from 'date-fns'
import type {
  VencimientoFiscal,
  VencimientoConCliente,
  ResultadoGeneracionVencimientos,
  VencimientosDelDia,
  RegistrarPagoData,
  RegistrarVEPData,
  MarcarPlanPagoData,
  DatosMaterializacion,
  Prorroga,
  ProrrogaInsert,
  VencimientoCalendarioOpcion,
  AlertaVencimiento,
  CrearAlertaData,
} from '@/types/vencimientos'
import { contarVencimientosPorEstado } from '@/lib/vencimientos-utils'
import { expandirOcurrencias } from '@/core/lib/recurrencia-eventos'
import type { FilaEstadistica } from '@/lib/vencimientos-utils'
import { fetchAllPages } from '@/lib/postgrest-chunks'
import type { MarcarPresentadoData } from '../components/calendario/MarcarPresentadoDialog'
import type {
  EventoCalendario,
  EventoCalendarioInsert,
  EventoCalendarioUpdate,
  EventoOcupado,
  InvitadosReunion,
  FiltrosVencimientos,
  FiltrosEventos,
  FiltrosCalendario,
  ItemCalendario,
  ItemsDelDia,
  TareaProyectada,
} from '../types/calendario'

/**
 * El evento se borró, en otra pestaña o por otro usuario, mientras se sincronizaba con Google
 * (ORR-538). No es un fallo de Google para reintentar: no queda evento. Lleva el que se intentó
 * sincronizar para que el alta pueda dejar su creación en la auditoría.
 */
export class EventoEliminadoError extends Error {
  readonly evento: EventoCalendario

  constructor(evento: EventoCalendario) {
    super('El evento se eliminó mientras se guardaba')
    this.name = 'EventoEliminadoError'
    this.evento = evento
  }
}

export const calendarioService = {
  // ===========================================
  // VENCIMIENTOS FISCALES
  // ===========================================

  /**
   * Obtener vencimientos activos (vencidos + próximos X días)
   * Incluye: pendientes con fecha pasada (vencidos) + pendientes/presentados futuros
   */
  async obtenerProximosVencimientos(
    client: SupabaseClient<Database>,
    organizacionId: string,
    dias: number = 30,
    incluirCompletados: boolean = false,
  ): Promise<VencimientoConCliente[]> {
    const hoy = new Date()
    const fechaLimite = addDays(hoy, dias)

    // Obtener vencimientos: vencidos (fecha pasada no presentados) + próximos X días
    // La vista ya tiene estado_efectivo calculado
    let query = client
      .from('vista_proximos_vencimientos')
      .select('*')
      .eq('organizacion_id', organizacionId)

    // ORR-73: si incluirCompletados=false (default), excluir presentados/pagados/plan_pago.
    // Si incluirCompletados=true, cargar TODO para que el filtro client-side funcione.
    if (!incluirCompletados) {
      query = query.not('estado_efectivo', 'in', '(presentado,pagado,plan_pago)')
    }

    query = query
      .or(
        `estado_efectivo.eq.vencido,` +
        `and(fecha_vencimiento.gte.${format(hoy, 'yyyy-MM-dd')},fecha_vencimiento.lte.${format(fechaLimite, 'yyyy-MM-dd')})`,
      )
      .order('fecha_vencimiento')

    const { data, error } = await query

    if (error) throw error
    return data as VencimientoConCliente[]
  },

  /**
   * Vencimientos presentados, pagados o en plan de pago cuyo día de presentación o de pago cae
   * cerca del rango `desde`-`hasta`. Pide un día de más antes y después del rango. Un cerrado sin
   * esas dos fechas entra si su vencimiento cae en el rango. El día exacto lo decide
   * `enPeriodoLista`. Lee todas las páginas.
   */
  async obtenerCerradosPorFechaCierre(
    client: SupabaseClient<Database>,
    organizacionId: string,
    desde: string,
    hasta: string,
  ): Promise<VencimientoConCliente[]> {
    const inicio = format(addDays(parseISO(desde), -1), 'yyyy-MM-dd')
    const finExcluido = format(addDays(parseISO(hasta), 2), 'yyyy-MM-dd')

    return fetchAllPages<VencimientoConCliente>(async (desdeFila, hastaFila) => {
      const { data, error } = await client
        .from('vista_proximos_vencimientos')
        .select('*')
        .eq('organizacion_id', organizacionId)
        .in('estado_efectivo', ['presentado', 'pagado', 'plan_pago'])
        .or(
          `and(fecha_presentacion.gte.${inicio},fecha_presentacion.lt.${finExcluido}),` +
            `and(fecha_pago.gte.${inicio},fecha_pago.lt.${finExcluido}),` +
            `and(fecha_presentacion.is.null,fecha_pago.is.null,` +
            `fecha_vencimiento.gte.${desde},fecha_vencimiento.lte.${hasta})`,
        )
        // El id de un virtual cambia en cada request. Las páginas se ordenan por columnas estables.
        .order('calendario_afip_id', { nullsFirst: false })
        .order('cliente_id')
        .order('id')
        .range(desdeFila, hastaFila)

      if (error) throw error
      return (data ?? []) as VencimientoConCliente[]
    })
  },

  /**
   * Obtener vencimientos de un mes específico
   */
  async obtenerVencimientosPorMes(
    client: SupabaseClient<Database>,
    organizacionId: string,
    year: number,
    month: number,
  ): Promise<VencimientoConCliente[]> {

    const inicioMes = startOfMonth(new Date(year, month - 1))
    const finMes = endOfMonth(new Date(year, month - 1))

    const { data, error } = await client
      .from('vista_proximos_vencimientos')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .not('estado_efectivo', 'in', '(presentado,pagado,plan_pago)')
      .gte('fecha_vencimiento', format(inicioMes, 'yyyy-MM-dd'))
      .lte('fecha_vencimiento', format(finMes, 'yyyy-MM-dd'))
      .order('fecha_vencimiento')

    if (error) throw error
    return data as VencimientoConCliente[]
  },

  /**
   * Listar vencimientos con filtros
   */
  async listarVencimientos(
    client: SupabaseClient<Database>,
    organizacionId: string,
    filtros?: FiltrosVencimientos,
  ): Promise<VencimientoConCliente[]> {

    let query = client
      .from('vista_proximos_vencimientos')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .not('estado_efectivo', 'in', '(presentado,pagado,plan_pago)')
      .order('fecha_vencimiento')

    if (filtros?.tipos_impuesto?.length) {
      query = query.in('tipo_impuesto', filtros.tipos_impuesto)
    }
    if (filtros?.responsables?.length) {
      query = query.in('cliente_responsable', filtros.responsables)
    }
    if (filtros?.estados?.length) {
      query = query.in('estado_efectivo', filtros.estados)
    }
    if (filtros?.clientes_ids?.length) {
      query = query.in('cliente_id', filtros.clientes_ids)
    }
    if (filtros?.desde) {
      query = query.gte('fecha_vencimiento', filtros.desde)
    }
    if (filtros?.hasta) {
      query = query.lte('fecha_vencimiento', filtros.hasta)
    }

    const { data, error } = await query

    if (error) throw error
    return data as VencimientoConCliente[]
  },

  /**
   * Obtener vencimientos vencidos
   */
  async obtenerVencimientosVencidos(
    client: SupabaseClient<Database>,
    organizacionId: string,
  ): Promise<VencimientoConCliente[]> {

    const { data, error } = await client
      .from('vista_vencimientos_vencidos')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .order('fecha_vencimiento')

    if (error) throw error
    return data as unknown as VencimientoConCliente[]
  },

  /**
   * Obtener vencimientos de un cliente
   */
  async obtenerVencimientosPorCliente(
    client: SupabaseClient<Database>,
    organizacionId: string,
    clienteId: string,
  ): Promise<VencimientoConCliente[]> {

    const { data, error } = await client
      .from('vista_proximos_vencimientos')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .eq('cliente_id', clienteId)
      .order('fecha_vencimiento', { ascending: true })

    if (error) throw error
    return data as VencimientoConCliente[]
  },

  /**
   * Marcar vencimiento como presentado (con materialización lazy si es virtual)
   */
  async marcarVencimientoPresentado(
    client: SupabaseClient<Database>,
    id: string,
    datos: MarcarPresentadoData,
    materializacion?: DatosMaterializacion
  ): Promise<VencimientoFiscal> {

    const { data, error } = await client.rpc('marcar_vencimiento_presentado', {
      p_vencimiento_id: id,
      p_fecha_presentacion: datos.fecha_presentacion,
      p_numero_presentacion: datos.numero_presentacion || undefined,
      p_observaciones: datos.observaciones || undefined,
      p_monto_determinado: datos.monto_determinado ?? undefined,
      p_calendario_afip_id: materializacion?.calendario_afip_id ?? undefined,
      p_cliente_id: materializacion?.cliente_id ?? undefined,
      p_forma_pago: datos.forma_pago ?? undefined,
    })

    if (error) throw error
    return data as unknown as VencimientoFiscal
  },

  /**
   * Crear vencimiento manual
   */
  async crearVencimiento(client: SupabaseClient<Database>, vencimiento: {
    cliente_id: string
    impuesto_nombre: string
    periodo: string
    fecha_vencimiento: string
    observaciones?: string
    created_by?: string
  }): Promise<VencimientoFiscal> {

    const insertData = {
      cliente_id: vencimiento.cliente_id,
      impuesto_nombre: vencimiento.impuesto_nombre,
      periodo: vencimiento.periodo,
      fecha_vencimiento: vencimiento.fecha_vencimiento,
      observaciones: vencimiento.observaciones,
      created_by: vencimiento.created_by,
      estado: 'pendiente' as const,
    }

    const { data, error } = await client
      .from('vencimientos_fiscales')
      .insert(insertData)
      .select()
      .single()

    if (error) throw error
    return data as VencimientoFiscal
  },

  /**
   * Actualizar vencimiento
   */
  async actualizarVencimiento(client: SupabaseClient<Database>, id: string, datos: Partial<VencimientoFiscal>): Promise<VencimientoFiscal> {

    const updateData = {
      ...datos,
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await client
      .from('vencimientos_fiscales')
      .update(updateData as Database['public']['Tables']['vencimientos_fiscales']['Update'])
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as VencimientoFiscal
  },

  /**
   * Eliminar vencimiento
   */
  async eliminarVencimiento(client: SupabaseClient<Database>, id: string): Promise<void> {
    // RPC con RBAC: verifica rol del caller antes de eliminar
    const { error } = await client.rpc('eliminar_vencimiento_seguro', { p_id: id })
    if (error) throw error
  },

  /**
   * Descartar un vencimiento virtual (proyección del calendario que no aplica).
   * ORR-178: registra una exclusión por (cliente, calendario_afip_id) — granularidad por
   * obligación, NO oculta las hermanas del mismo período. RPC con RBAC. Devuelve el id de
   * la fila de descarte (para deshacer / restaurar).
   */
  async descartarVencimiento(client: SupabaseClient<Database>, calendarioAfipId: string, clienteId: string): Promise<string> {
    const { data, error } = await client.rpc('descartar_vencimiento', {
      p_calendario_afip_id: calendarioAfipId,
      p_cliente_id: clienteId,
    })
    if (error) throw error
    return data as string
  },

  /**
   * ORR-234: descartar un vencimiento que NO viene del calendario AFIP (recurrentes y manuales).
   * Esas filas no tienen calendario_afip_id, así que la RPC de arriba no las alcanza: acá se marca
   * la columna `descartado` de la propia fila. Devuelve su id, que es lo que espera restaurar.
   */
  async descartarVencimientoFisico(client: SupabaseClient<Database>, id: string): Promise<string> {
    const { data, error } = await client.rpc('descartar_vencimiento_fisico', { p_id: id })
    if (error) throw error
    return data as string
  },

  /**
   * ORR-232: alertas que el usuario programó. Se filtran por usuario porque una alerta es
   * personal: el destinatario es siempre quien la creó.
   */
  async obtenerAlertas(
    client: SupabaseClient<Database>,
    organizacionId: string,
    usuarioId: string,
  ): Promise<AlertaVencimiento[]> {
    const { data, error } = await client
      .from('vencimiento_alertas')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .eq('usuario_id', usuarioId)
      .eq('activo', true)

    if (error) throw error
    return (data || []) as AlertaVencimiento[]
  },

  /**
   * ORR-232: programar una alerta. El ancla la decide el caller según la fila:
   * calendario_afip_id para las del calendario AFIP, vencimiento_fiscal_id para las
   * recurrentes, impuesto_id cuando el alcance es todos_los_siguientes.
   */
  async crearAlerta(
    client: SupabaseClient<Database>,
    organizacionId: string,
    usuarioId: string,
    datos: CrearAlertaData,
  ): Promise<AlertaVencimiento> {
    const { data, error } = await client
      .from('vencimiento_alertas')
      .insert({
        organizacion_id: organizacionId,
        usuario_id: usuarioId,
        cliente_id: datos.cliente_id,
        alcance: datos.alcance,
        dias_antes: datos.dias_antes,
        calendario_afip_id: datos.calendario_afip_id ?? null,
        vencimiento_fiscal_id: datos.vencimiento_fiscal_id ?? null,
        impuesto_id: datos.impuesto_id ?? null,
      })
      .select()
      .single()

    if (error) throw error
    return data as AlertaVencimiento
  },

  /**
   * ORR-232: quitar una alerta. Se borra en vez de desactivarla para que el usuario pueda
   * volver a programarla sobre el mismo vencimiento; los envíos ya emitidos caen con ella.
   */
  async eliminarAlerta(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client.from('vencimiento_alertas').delete().eq('id', id)
    if (error) throw error
  },

  /**
   * Restaurar un vencimiento descartado. RPC con RBAC. Acepta el id de la exclusión nueva
   * o el id de un vencimiento_fiscal descartado por el mecanismo legacy (lo resuelve por id).
   */
  async restaurarVencimiento(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client.rpc('restaurar_vencimiento', { p_id: id })
    if (error) throw error
  },

  /**
   * Obtener los vencimientos descartados (papelera, filtro "Descartado"). Une la tabla de
   * exclusión nueva (por-obligación) y los vf.descartado legacy vía
   * vista_vencimientos_descartados. estado_efectivo='descartado'; `id` sirve para restaurar.
   */
  async obtenerVencimientosDescartados(
    client: SupabaseClient<Database>,
    organizacionId: string,
  ): Promise<VencimientoConCliente[]> {
    const { data, error } = await client
      .from('vista_vencimientos_descartados')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .order('fecha_vencimiento', { ascending: true })

    if (error) throw error
    return (data ?? []) as unknown as VencimientoConCliente[]
  },

  /**
   * Revertir una presentación marcada por error → vuelve a 'pendiente'.
   * RPC con RBAC + guardas (bloquea si ya tiene pago/VEP/plan de pago).
   */
  async revertirVencimientoPresentado(client: SupabaseClient<Database>, id: string): Promise<VencimientoFiscal> {
    const { data, error } = await client.rpc('revertir_vencimiento_presentado', { p_id: id })
    if (error) throw error
    return data as VencimientoFiscal
  },

  /**
   * Actualizar estado de vencimientos vencidos
   */
  async actualizarEstadoVencidos(client: SupabaseClient<Database>): Promise<number> {

    const { data, error } = await client.rpc('actualizar_estado_vencidos')

    if (error) throw error
    return (data as number) ?? 0
  },

  /**
   * Obtener estadísticas de vencimientos (ORR-96)
   * Usa vista_proximos_vencimientos (misma fuente que la planilla principal — incluye virtuales del calendario AFIP).
   * Las 5 tarjetas muestran métricas del mes calendario en curso.
   */
  /**
   * Las filas con las que se arman las cinco tarjetas de la planilla.
   *
   * Devuelve filas y no los números ya contados (ORR-382): el conteo depende del interruptor
   * Míos / Todo el estudio Y del reparto de conceptos, y ninguno de los dos puede viajar en la
   * `queryKey` de forma confiable. Contando acá, un cambio de reparto dejaba las tarjetas con los
   * dueños viejos hasta el refetch siguiente. Con las filas crudas el cálculo se rehace en el
   * cliente apenas cambia el scoping, sin depender de ninguna invalidación.
   *
   * Las tres últimas columnas son las que resuelven a quién le corresponde cada fila; sin ellas
   * los recuadros contaban todo el estudio aunque la planilla estuviera filtrada.
   */
  async obtenerFilasEstadisticas(
    client: SupabaseClient<Database>,
    organizacionId: string,
  ): Promise<FilaEstadistica[]> {

    // Misma fuente que la planilla principal, con los virtuales del calendario AFIP. Lee todas las páginas.
    return fetchAllPages<FilaEstadistica>(async (desde, hasta) => {
      const { data, error } = await client
        .from('vista_proximos_vencimientos')
        .select(
          'estado, fecha_vencimiento, fecha_presentacion, fecha_pago, presentado, pagado, cliente_id, impuesto_id, obligacion_recurrente_id',
        )
        .eq('organizacion_id', organizacionId)
        // El id de un virtual cambia en cada request. Las páginas se ordenan por columnas estables.
        .order('calendario_afip_id', { nullsFirst: false })
        .order('cliente_id')
        .order('id')
        .range(desde, hasta)

      if (error) throw error
      return (data ?? []) as unknown as FilaEstadistica[]
    })
  },

  /**
   * Obtener tipos de impuesto disponibles para el filtro de la planilla.
   *
   * Lee de la misma vista que alimenta la planilla y de la misma columna contra la
   * que filtra `obtenerProximosVencimientos` (`tipo_impuesto`). Antes leía
   * `vencimientos_config`, tabla legacy sin relación con lo que se muestra: ofrecía
   * 8 impuestos sin una sola fila y omitía 7 que sí estaban (entre ellos CM-05 y
   * todas las obligaciones recurrentes).
   */
  async obtenerTiposImpuesto(client: SupabaseClient<Database>): Promise<string[]> {
    // REVISIT-IF: haya más de una organización en PROD. No filtra por `organizacion_id`
    // (la firma no lo recibe, igual que la versión anterior), así que el desplegable
    // ofrecería tipos de otras orgs. Hoy hay una sola, por eso no se amplía el scope acá.
    //
    // El DISTINCT lo hace Postgres, NO el cliente: leer la vista y deduplicar acá se
    // truncaba en las 1000 filas que devuelve PostgREST por defecto, y la vista tiene
    // ~1500 — los conceptos que caían más allá de esa fila nunca llegaban al desplegable
    // (medido en DEV: 9 de 15). La RPC devuelve ~16 filas y no tiene ese techo.
    const { data, error } = await client.rpc('obtener_tipos_impuesto_vencimientos')

    if (error) throw error

    return (data ?? [])
      .map(d => d.tipo_impuesto)
      .filter((t): t is string => Boolean(t))
      .sort((a, b) => a.localeCompare(b, 'es'))
  },

  // ===========================================
  // CICLO DE PAGO
  // ===========================================

  /**
   * Registrar pago de un vencimiento (con materialización lazy si es virtual)
   */
  async registrarPago(
    client: SupabaseClient<Database>,
    id: string,
    datos: RegistrarPagoData,
    materializacion?: DatosMaterializacion
  ): Promise<VencimientoFiscal> {
    const { data, error } = await client.rpc('registrar_pago_vencimiento', {
      p_id: id,
      p_monto_pagado: datos.monto_pagado ?? undefined,
      p_fecha_pago: datos.fecha_pago,
      p_forma_pago: datos.forma_pago ?? undefined,
      p_referencia_pago: datos.referencia_pago ?? undefined,
      p_observaciones: datos.observaciones ?? undefined,
      p_calendario_afip_id: materializacion?.calendario_afip_id ?? undefined,
      p_cliente_id: materializacion?.cliente_id ?? undefined,
    })

    if (error) throw error
    return data as unknown as VencimientoFiscal
  },

  /**
   * Registrar VEP (con materialización lazy si es virtual)
   */
  async registrarVEP(
    client: SupabaseClient<Database>,
    id: string,
    datos: RegistrarVEPData,
    materializacion?: DatosMaterializacion
  ): Promise<VencimientoFiscal> {
    const { data, error } = await client.rpc('registrar_vep_vencimiento', {
      p_id: id,
      p_vep_numero: datos.vep_numero,
      p_vep_monto: datos.vep_monto ?? undefined,
      p_vep_fecha_vencimiento: datos.vep_fecha_vencimiento ?? undefined,
      p_calendario_afip_id: materializacion?.calendario_afip_id ?? undefined,
      p_cliente_id: materializacion?.cliente_id ?? undefined,
    })

    if (error) throw error
    return data as unknown as VencimientoFiscal
  },

  /**
   * Marcar vencimiento en plan de pago (con materialización lazy si es virtual)
   */
  async marcarPlanPago(
    client: SupabaseClient<Database>,
    id: string,
    datos: MarcarPlanPagoData,
    materializacion?: DatosMaterializacion
  ): Promise<VencimientoFiscal> {
    const { data, error } = await client.rpc('marcar_plan_pago_vencimiento', {
      p_id: id,
      p_cuotas: datos.cuotas ?? undefined,
      p_referencia: datos.referencia ?? undefined,
      p_observaciones: datos.observaciones ?? undefined,
      p_calendario_afip_id: materializacion?.calendario_afip_id ?? undefined,
      p_cliente_id: materializacion?.cliente_id ?? undefined,
    })

    if (error) throw error
    return data as unknown as VencimientoFiscal
  },

  // ===========================================
  // EVENTOS CALENDARIO
  // ===========================================

  /**
   * Listar eventos con filtros
   */
  async listarEventos(client: SupabaseClient<Database>, filtros?: FiltrosEventos): Promise<EventoCalendario[]> {

    let query = client
      .from('eventos_calendario')
      .select('*')
      .order('fecha_inicio')

    if (filtros?.organizacion_id) {
      query = query.eq('organizacion_id', filtros.organizacion_id)
    }
    if (filtros?.tipo) {
      query = query.eq('tipo', filtros.tipo)
    }
    if (filtros?.estado) {
      query = query.eq('estado', filtros.estado)
    }
    if (filtros?.workspace) {
      query = query.eq('workspace', filtros.workspace)
    }
    if (filtros?.desde) {
      query = query.gte('fecha_inicio', filtros.desde)
    }
    if (filtros?.hasta) {
      query = query.lte('fecha_inicio', filtros.hasta)
    }
    if (filtros?.vinculado_tipo) {
      query = query.eq('vinculado_tipo', filtros.vinculado_tipo)
    }
    if (filtros?.vinculado_id) {
      query = query.eq('vinculado_id', filtros.vinculado_id)
    }

    const { data, error } = await query

    if (error) throw error
    return data as EventoCalendario[]
  },

  /**
   * Obtener eventos de un mes
   */
  async obtenerEventosPorMes(client: SupabaseClient<Database>, organizacionId: string, year: number, month: number, workspace?: string): Promise<EventoCalendario[]> {

    const inicioMes = startOfMonth(new Date(year, month - 1))
    const finMes = endOfMonth(new Date(year, month - 1))

    let query = client
      .from('eventos_calendario')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .gte('fecha_inicio', format(inicioMes, 'yyyy-MM-dd'))
      .lte('fecha_inicio', format(finMes, 'yyyy-MM-dd'))
      .order('fecha_inicio')

    if (workspace) {
      query = query.eq('workspace', workspace)
    }

    const { data, error } = await query

    if (error) throw error
    return data as EventoCalendario[]
  },

  /**
   * Series de eventos que pueden tener ocurrencias en el mes consultado (ORR-383).
   *
   * Va aparte de `obtenerEventosPorMes` porque esa filtra por `fecha_inicio` DENTRO del mes: una
   * serie que arrancó en agosto no vuelve en la consulta de octubre, y expandir solo lo que devuelve
   * dejaría la repetición invisible desde el mes siguiente al alta.
   *
   * El corte por `UNTIL` no se puede hacer en SQL —vive adentro del texto del RRULE—, así que acá se
   * trae todo lo que arrancó antes del fin del mes y el filtro fino lo aplica `expandirOcurrencias`.
   *
   * El límite es `lt` contra el día SIGUIENTE, no `lte` contra el último: `fecha_inicio` es un
   * timestamp y `lte 'yyyy-MM-dd'` lo compara contra la medianoche que ABRE ese día, así que una
   * serie que arranca a las 10 de la mañana del 31 quedaba afuera. Y como los recurrentes ya no
   * salen por la consulta mensual, afuera de acá no había otro camino para que se dibujaran.
   */
  async obtenerSeriesVigentes(
    client: SupabaseClient<Database>,
    organizacionId: string,
    finMes: Date,
    workspace?: string
  ): Promise<EventoCalendario[]> {
    let query = client
      .from('eventos_calendario')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .eq('recurrente', true)
      .lt('fecha_inicio', format(addDays(finMes, 1), 'yyyy-MM-dd'))
      .order('fecha_inicio')

    if (workspace) {
      query = query.eq('workspace', workspace)
    }

    const { data, error } = await query

    if (error) throw error
    return data as EventoCalendario[]
  },

  /**
   * Obtener evento por ID
   */
  async obtenerEventoPorId(client: SupabaseClient<Database>, id: string): Promise<EventoCalendario> {

    const { data, error } = await client
      .from('eventos_calendario')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as EventoCalendario
  },

  /**
   * Crear evento
   */
  async crearEvento(client: SupabaseClient<Database>, evento: EventoCalendarioInsert): Promise<EventoCalendario> {

    const { data, error } = await client
      .from('eventos_calendario')
      .insert(evento as Database['public']['Tables']['eventos_calendario']['Insert'])
      .select()
      .single()

    if (error) throw error
    return data as unknown as EventoCalendario
  },

  /**
   * Crea la videollamada en el Google Calendar del estudio (ORR-227 fase 3).
   *
   * Best-effort a propósito: cuando esto corre la reunión local YA existe, así que un
   * fallo de Google nunca se propaga — se guarda en `google_sync_error` para que la UI
   * ofrezca reintentar. Al revés (Google primero) quedarían reuniones en el calendario
   * del estudio que BrainHub no conoce y nadie puede cancelar desde la app.
   *
   * `meet_url` y `google_event_id` los guarda la function, también el id parcial de un éxito
   * incompleto (ORR-537). La base no deja que el cliente los cambie, porque son los que la
   * function usa para decidir qué reunión toca. Acá solo se guarda el error, que puede no
   * llegar nunca a la function: un fallo de red o de token.
   *
   * Idempotente: si el evento ya tiene `google_event_id`, la function actualiza esa
   * reunión en vez de crear una segunda.
   *
   * - `token`: Auth0 JWT (obtenido vía `getAccessTokenSilently`).
   */
  async sincronizarReunionConGoogle(
    client: SupabaseClient<Database>,
    token: string,
    evento: EventoCalendario,
  ): Promise<EventoCalendario> {
    try {
      const response = await fetch('/.netlify/functions/google-calendar-evento', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventoId: evento.id,
          titulo: evento.titulo,
          descripcion: evento.descripcion,
          fechaInicio: evento.fecha_inicio,
          fechaFin: evento.fecha_fin ?? evento.fecha_inicio,
          participantes: evento.participantes,
          reglaRecurrencia: evento.regla_recurrencia,
        }),
      })

      if (!response.ok) {
        // El body recién se lee acá: trae el mensaje que armó la function.
        const error = await response.json().catch(() => ({}))
        if (error?.codigo === 'evento_eliminado') throw new EventoEliminadoError(evento)
        throw new Error(
          typeof error?.error === 'string'
            ? error.error
            : `Error ${response.status} al crear la reunión en Google`,
        )
      }

      const datos = await response.json()

      // La fila ya quedó así en la base: lo guardó la function.
      return { ...evento, meet_url: datos.meetUrl, google_event_id: datos.googleEventId, google_sync_error: null }
    } catch (error) {
      // No hay fila donde guardar el error ni nada que reintentar: lo resuelve quien llamó.
      if (error instanceof EventoEliminadoError) throw error
      const mensaje = error instanceof Error ? error.message : 'Error desconocido'
      console.error('[calendario] No se pudo crear la reunión en Google:', mensaje)

      // El evento sobrevive al fallo. Si ni siquiera se puede guardar el error,
      // se devuelve el evento tal cual: perder el detalle no justifica romper el alta.
      // El update devuelve la fila, con el id parcial que pudo haber guardado la function.
      try {
        return await this.actualizarEvento(client, evento.id, { google_sync_error: mensaje })
      } catch {
        return evento
      }
    }
  },

  /**
   * Devuelve los eventos del calendario del estudio que se pisan con la ventana (ORR-301).
   *
   * No bloquea el alta: si Google no contesta se devuelve `[]` y la reunión se agenda igual.
   * Que la consulta falle no es razón para impedir agendar — el aviso es una ayuda, no una
   * validación. El llamador distingue el caso por el `error` que se propaga en el rechazo.
   *
   * - `token`: Auth0 JWT (obtenido vía `getAccessTokenSilently`).
   */
  async consultarDisponibilidad(
    token: string,
    organizacionId: string,
    fechaInicio: string,
    fechaFin: string,
  ): Promise<EventoOcupado[]> {
    const response = await fetch('/.netlify/functions/google-calendar-disponibilidad', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ fechaInicio, fechaFin, organizacionId }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(
        typeof error?.error === 'string'
          ? error.error
          : `Error ${response.status} al consultar la agenda del estudio`,
      )
    }

    const datos = await response.json()
    return (datos.ocupado ?? []) as EventoOcupado[]
  },

  /**
   * Lee en Google Calendar la respuesta de cada invitado de una reunión (ORR-544).
   *
   * La function lee la reunión que guarda la fila y exige `verCalendario` en su organización.
   * Una reunión que Google no tiene o canceló devuelve `enGoogle: false`, no un error.
   * Tira si la function falla o si la respuesta llega sin la lista de invitados.
   *
   * - `token`: Auth0 JWT (obtenido vía `getAccessTokenSilently`).
   */
  async leerInvitadosReunion(token: string, eventoId: string): Promise<InvitadosReunion> {
    const response = await fetch('/.netlify/functions/google-calendar-invitados', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ eventoId }),
    })

    const datos = await response.json().catch(() => ({}))
    if (!response.ok) {
      if (datos?.codigo === 'sin_reunion_en_google') {
        return { enGoogle: false, leidoAt: new Date().toISOString() }
      }
      throw new Error(
        typeof datos?.error === 'string'
          ? datos.error
          : `Error ${response.status} al leer los invitados en Google`,
      )
    }

    const ESTADOS: unknown[] = ['aceptado', 'rechazado', 'tal_vez', 'sin_respuesta']
    const completa =
      Array.isArray(datos?.invitados) &&
      datos.invitados.every(
        (i: { email?: unknown; estado?: unknown }) => typeof i?.email === 'string' && ESTADOS.includes(i.estado),
      ) &&
      Array.isArray(datos?.faltanEnGoogle) &&
      datos.faltanEnGoogle.every((email: unknown) => typeof email === 'string') &&
      typeof datos?.leidoAt === 'string' &&
      !Number.isNaN(Date.parse(datos.leidoAt))
    if (!completa) {
      throw new Error('Los invitados no se pudieron leer porque la respuesta de Google llegó incompleta')
    }
    return {
      enGoogle: true,
      invitados: datos.invitados,
      faltanEnGoogle: datos.faltanEnGoogle,
      leidoAt: datos.leidoAt,
    }
  },

  /**
   * Actualizar evento
   */
  async actualizarEvento(client: SupabaseClient<Database>, id: string, datos: EventoCalendarioUpdate): Promise<EventoCalendario> {

    const { data, error } = await client
      .from('eventos_calendario')
      .update({ ...datos, updated_at: new Date().toISOString() } as Database['public']['Tables']['eventos_calendario']['Update'])
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as unknown as EventoCalendario
  },

  /**
   * Cancela la reunión en el Google Calendar del estudio.
   *
   * Al revés que `sincronizarReunionConGoogle`, esto NO es best-effort: tira si Google
   * rechaza la cancelación, para que el llamador aborte antes de borrar la fila local.
   * Borrar primero acá dejaría el evento y su Meet vivos en la agenda del estudio, sin
   * el `google_event_id` para encontrarlos y sin forma de cancelarlos desde la app —
   * y el invitado externo seguiría viendo la reunión en pie.
   *
   * Un evento que en Google ya no existe cuenta como cancelado (la function traduce el
   * 404/410), así que un borrado a medias se puede reintentar sin quedar trabado.
   */
  async cancelarReunionEnGoogle(token: string, googleEventId: string): Promise<void> {
    const response = await fetch('/.netlify/functions/google-calendar-evento', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ googleEventId }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(
        typeof error?.error === 'string'
          ? error.error
          : `Error ${response.status} al cancelar la reunión en Google`,
      )
    }
  },

  /**
   * Eliminar evento
   */
  async eliminarEvento(client: SupabaseClient<Database>, id: string): Promise<void> {
    // RPC con RBAC: verifica rol del caller antes de eliminar
    const { error } = await client.rpc('eliminar_evento_seguro', { p_id: id })
    if (error) throw error
  },

  // ===========================================
  // CALENDARIO UNIFICADO
  // ===========================================

  /**
   * Tareas de un mes con `fecha_limite` en rango, para el calendario unificado (ORR-228).
   *
   * `vista_tareas_completas` no tiene `organizacion_id` — a diferencia de vencimientos y
   * eventos, las tareas no scopean por organización hoy (una sola org en PROD). Filtrar
   * por eso rompería silenciosamente el día que haya una segunda.
   */
  async obtenerTareasPorMes(
    client: SupabaseClient<Database>,
    year: number,
    month: number,
    workspace?: string,
  ) {
    const inicioMes = startOfMonth(new Date(year, month - 1))
    const finMes = endOfMonth(new Date(year, month - 1))

    let query = client
      .from('vista_tareas_completas')
      .select('*')
      .neq('estado', 'completada')
      .gte('fecha_limite', format(inicioMes, 'yyyy-MM-dd'))
      .lte('fecha_limite', format(finMes, 'yyyy-MM-dd'))
      .order('fecha_limite')

    if (workspace) {
      query = query.eq('workspace', workspace)
    }

    const { data, error } = await query

    if (error) throw error
    return data ?? []
  },

  /**
   * Obtener items del calendario (vencimientos + eventos + tareas) para un mes
   */
  async obtenerItemsPorMes(client: SupabaseClient<Database>,
    organizacionId: string,
    year: number,
    month: number,
    filtros?: FiltrosCalendario
  ): Promise<ItemCalendario[]> {
    const items: ItemCalendario[] = []

    // Obtener vencimientos (a menos que se excluyan)
    if (filtros?.incluir_vencimientos !== false) {
      const vencimientos = await this.obtenerVencimientosPorMes(client, organizacionId, year, month)
      for (const v of vencimientos) {
        items.push({
          id: v.id ?? '',
          tipo: 'vencimiento',
          titulo: `${v.tipo_impuesto} - ${v.cliente_nombre || 'Sin cliente'}`,
          descripcion: v.observaciones || null,
          fecha: v.fecha_vencimiento ?? '',
          fecha_fin: null,
          todo_el_dia: true,
          estado: v.estado ?? 'pendiente',
          color: v.estado === 'vencido' ? '#EF4444' : v.estado === 'presentado' ? '#10B981' : '#F59E0B',
          workspace: 'contable',
          cliente_id: v.cliente_id || null,
          cliente_nombre: v.cliente_nombre || null,
          tipo_impuesto: v.tipo_impuesto,
          periodo: v.periodo,
          datos_originales: v as unknown as Record<string, unknown>,
        })
      }
    }

    // Obtener eventos (a menos que se excluyan)
    if (filtros?.incluir_eventos !== false) {
      const finMes = endOfMonth(new Date(year, month - 1))

      // Los bordes de la ventana se arman en UTC porque los timestamps de la tabla son HORA DE
      // PARED con sufijo `+00:00` (ver `src/core/lib/recurrencia-eventos.ts`). Con `startOfMonth`
      // local, en Argentina el borde queda 3 h adelantado y la ocurrencia del día 1 a medianoche
      // cae afuera del mes.
      const desdeVentana = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0))
      const hastaVentana = new Date(Date.UTC(year, month, 0, 23, 59, 59))

      const [eventos, series] = await Promise.all([
        this.obtenerEventosPorMes(client, organizacionId, year, month, filtros?.workspace),
        this.obtenerSeriesVigentes(client, organizacionId, finMes, filtros?.workspace),
      ])

      const armarItem = (e: EventoCalendario, id: string, inicio: string, fin: string | null) => ({
        id,
        tipo: 'evento' as const,
        titulo: e.titulo,
        descripcion: e.descripcion,
        fecha: inicio,
        fecha_fin: fin,
        todo_el_dia: e.todo_el_dia,
        estado: e.estado,
        color: e.color,
        workspace: e.workspace,
        ubicacion: e.ubicacion,
        participantes: e.participantes,
        tipo_evento: e.tipo,
        meet_url: e.meet_url,
        // La ocurrencia viaja en campos APARTE, sin pisar `fecha_inicio`. El detalle usa esos
        // para mostrar y el formulario de edición sigue leyendo las del maestro: si se pisaran,
        // guardar un cambio de título desde la ocurrencia del 27 reanclaría la serie entera ahí.
        datos_originales: { ...e, ocurrencia_inicio: inicio, ocurrencia_fin: fin },
      })

      // `tipos_evento` se declaraba en FiltrosCalendario y no se aplicaba en ninguna de las dos
      // ramas: quien pedía solo reuniones recibía además audiencias, citas y recordatorios.
      const tiposPedidos = filtros?.tipos_evento
      const tipoPedido = (e: EventoCalendario) => !tiposPedidos || tiposPedidos.includes(e.tipo)

      // Los recurrentes salen por la expansión, no por acá: incluirlos en las dos ramas dibujaría
      // dos veces la ocurrencia del mes en que se dio de alta la serie.
      for (const e of eventos) {
        if (e.recurrente || !tipoPedido(e)) continue
        items.push(armarItem(e, e.id, e.fecha_inicio, e.fecha_fin))
      }

      for (const serie of series) {
        if (!tipoPedido(serie)) continue

        const ocurrencias = expandirOcurrencias({
          fechaInicio: serie.fecha_inicio,
          fechaFin: serie.fecha_fin,
          regla: serie.regla_recurrencia,
          desde: desdeVentana,
          hasta: hastaVentana,
        })

        for (const o of ocurrencias) {
          // Prefijado y compuesto, igual que `proy:` de ORR-331: el id de una ocurrencia nunca
          // puede confundirse con el UUID de la fila ni colisionar con otra semana de la serie.
          items.push(armarItem(serie, `rec:${serie.id}:${o.inicio}`, o.inicio, o.fin))
        }
      }
    }

    // Obtener tareas (a menos que se excluyan)
    if (filtros?.incluir_tareas !== false) {
      const tareas = await this.obtenerTareasPorMes(client, year, month, filtros?.workspace)
      for (const t of tareas) {
        if (!t.fecha_limite) continue
        items.push({
          id: t.id ?? '',
          tipo: 'tarea',
          titulo: t.titulo ?? '(sin título)',
          descripcion: t.descripcion,
          fecha: t.fecha_limite,
          fecha_fin: null,
          todo_el_dia: true,
          estado: t.estado ?? 'pendiente',
          color: null,
          workspace: t.workspace,
          asignado_a: t.asignado_a,
          prioridad: t.prioridad,
          datos_originales: t as unknown as Record<string, unknown>,
        })
      }
    }

    // ORR-331: las ocurrencias FUTURAS de las recurrentes, que todavía no son filas. El motor
    // materializa solo hasta hoy, así que sin esto el mes que viene sale vacío aunque la serie
    // esté programada. Van bajo el mismo filtro que las tareas reales: quien apaga tareas apaga
    // las dos.
    if (filtros?.incluir_tareas !== false) {
      const proyectadas = await this.obtenerTareasProyectadasPorMes(
        client, organizacionId, year, month, filtros?.workspace
      )
      for (const p of proyectadas) {
        items.push({
          // Prefijado y compuesto a propósito: un id de proyección nunca puede confundirse con
          // un UUID de la base ni colisionar con otra ocurrencia de la misma serie.
          // ORR-364: el cliente entra al id porque una serie con etiqueta emite N proyecciones
          // para la MISMA fecha, una por cliente del grupo. Sin él son N items con el mismo id.
          id: `proy:${p.tarea_recurrente_id}:${p.fecha}:${p.cliente_id ?? '-'}`,
          tipo: 'tarea_proyectada',
          titulo: p.titulo,
          descripcion: p.descripcion,
          fecha: p.fecha,
          fecha_fin: null,
          todo_el_dia: true,
          estado: 'programada',
          color: null,
          workspace: p.workspace,
          cliente_id: p.cliente_id,
          asignado_a: p.asignado_a,
          prioridad: p.prioridad,
          datos_originales: p as unknown as Record<string, unknown>,
        })
      }
    }

    // Ordenar por fecha
    return items.sort((a, b) => a.fecha.localeCompare(b.fecha))
  },

  /**
   * Ocurrencias futuras de las recurrentes activas, calculadas sin materializarlas (ORR-331).
   *
   * La aritmética vive entera en `calcular_proxima_fecha_tarea`, del lado de la base: replicarla
   * acá daría dos calendarios que se desincronizan. La RPC ya excluye lo que está materializado.
   *
   * Ante error devuelve lista vacía en vez de propagar: perder la proyección degrada la vista,
   * romper el calendario entero por ella sería peor.
   */
  async obtenerTareasProyectadasPorMes(
    client: SupabaseClient<Database>,
    organizacionId: string,
    year: number,
    month: number,
    workspace?: string
  ): Promise<TareaProyectada[]> {
    const inicioMes = startOfMonth(new Date(year, month - 1))
    const finMes = endOfMonth(new Date(year, month - 1))

    // `proyectar_tareas_recurrentes` todavía no figura en `database.ts`: los types están atrás de
    // PROD y regenerarlos hoy rompe la compilación (ORR-305). El cast se quita cuando se resuelva.
    // Se castea el CLIENTE, no el método: sacar `rpc` a una variable lo desprende de su receptor
    // y supabase-js usa `this` adentro — compila, pasa los tests y explota recién en runtime.
    const rpcClient = client as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>
    }

    // try/catch además del `error` devuelto: supabase-js resuelve algunos fallos y LANZA otros
    // (red caída, cliente sin `rpc`). Mirar solo `error` deja pasar la excepción hasta
    // `obtenerItemsPorMes` y tumba el calendario entero por una proyección — que es exactamente
    // lo que no puede pasar. Lo cazó un test de ORR-228 que no tocaba esto.
    try {
      const { data, error } = await rpcClient.rpc('proyectar_tareas_recurrentes', {
        p_organizacion_id: organizacionId,
        p_desde: format(inicioMes, 'yyyy-MM-dd'),
        p_hasta: format(finMes, 'yyyy-MM-dd'),
      })

      if (error) {
        console.error('Error proyectando tareas recurrentes:', error)
        return []
      }

      const proyectadas = (data ?? []) as TareaProyectada[]
      // El workspace se filtra acá, no en la RPC: es el mismo recorte que ya aplican los eventos y
      // las tareas reales, y sin esto un pedido por workspace devolvía proyectadas de los otros.
      return workspace ? proyectadas.filter((p) => p.workspace === workspace) : proyectadas
    } catch (err) {
      console.error('Error proyectando tareas recurrentes:', err)
      return []
    }
  },

  /**
   * Obtener items agrupados por día
   */
  async obtenerItemsPorDia(client: SupabaseClient<Database>,
    organizacionId: string,
    year: number,
    month: number,
    filtros?: FiltrosCalendario
  ): Promise<ItemsDelDia[]> {
    const items = await this.obtenerItemsPorMes(client, organizacionId, year, month, filtros)

    // Agrupar por fecha
    const porDia = new Map<string, ItemCalendario[]>()

    for (const item of items) {
      const fecha = item.fecha.split('T')[0] // Extraer solo la fecha
      if (!porDia.has(fecha)) {
        porDia.set(fecha, [])
      }
      porDia.get(fecha)!.push(item)
    }

    // Convertir a array
    const resultado: ItemsDelDia[] = []
    for (const [fecha, itemsDelDia] of porDia) {
      resultado.push({
        fecha,
        items: itemsDelDia,
        totalVencimientos: itemsDelDia.filter(i => i.tipo === 'vencimiento').length,
        totalEventos: itemsDelDia.filter(i => i.tipo === 'evento').length,
        totalTareas: itemsDelDia.filter(i => i.tipo === 'tarea').length,
        totalPendientes: itemsDelDia.filter(i => i.estado === 'pendiente').length,
      })
    }

    return resultado.sort((a, b) => a.fecha.localeCompare(b.fecha))
  },

  /**
   * Obtener vencimientos agrupados por día (compatibilidad)
   */
  async obtenerVencimientosPorDia(
    client: SupabaseClient<Database>,
    organizacionId: string,
    year: number,
    month: number,
  ): Promise<VencimientosDelDia[]> {
    const vencimientos = await this.obtenerVencimientosPorMes(client, organizacionId, year, month)

    const porDia = new Map<string, VencimientoConCliente[]>()

    for (const v of vencimientos) {
      const fecha = v.fecha_vencimiento ?? ''
      if (!fecha) continue
      if (!porDia.has(fecha)) {
        porDia.set(fecha, [])
      }
      porDia.get(fecha)!.push(v)
    }

    const resultado: VencimientosDelDia[] = []
    for (const [fecha, vencs] of porDia) {
      resultado.push({
        fecha,
        vencimientos: vencs,
        // Clasificar por estado_efectivo: `estado` crudo nunca vale 'vencido', así
        // que los vencidos reales quedaban sin contar (grid mensual sin rojo).
        ...contarVencimientosPorEstado(vencs),
      })
    }

    return resultado.sort((a, b) => a.fecha.localeCompare(b.fecha))
  },

  // =====================================================
  // FERIADOS
  // =====================================================

  /**
   * Listar feriados por año
   */
  async listarFeriados(
    client: SupabaseClient<Database>,
    anio: number,
    jurisdiccion?: string,
  ) {
    let query = client
      .from('feriados')
      .select('*')
      .eq('anio', anio)
      .order('fecha', { ascending: true })

    if (jurisdiccion) {
      query = query.or(`jurisdiccion.is.null,jurisdiccion.eq.${jurisdiccion}`)
    }

    const { data, error } = await query
    if (error) throw error
    return data
  },

  /**
   * Crear feriado
   */
  async crearFeriado(
    client: SupabaseClient<Database>,
    feriado: { fecha: string; nombre: string; tipo: string; jurisdiccion?: string | null },
  ) {
    const { data, error } = await client
      .from('feriados')
      .insert(feriado)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Eliminar feriado
   */
  async eliminarFeriado(client: SupabaseClient<Database>, id: string) {
    const { error } = await client.from('feriados').delete().eq('id', id)
    if (error) throw error
  },

  // ===========================================
  // PRÓRROGAS FISCALES
  // ===========================================

  /**
   * Listar prórrogas activas
   */
  async listarProrrogas(client: SupabaseClient<Database>, organizacionId: string, filtros?: { impuesto_id?: string; periodo?: string }): Promise<Prorroga[]> {
    let query = client
      .from('prorrogas_fiscales')
      .select('*')
      .eq('activo', true)
      .eq('organizacion_id', organizacionId)
      .order('created_at', { ascending: false })

    if (filtros?.impuesto_id) {
      query = query.eq('impuesto_id', filtros.impuesto_id)
    }
    if (filtros?.periodo) {
      query = query.eq('periodo', filtros.periodo)
    }

    const { data, error } = await query
    if (error) throw error
    return data as Prorroga[]
  },

  /**
   * Listar las filas del calendario AFIP de un impuesto, para elegir cuál prorrogar.
   *
   * ORR-277: reemplaza el tipeo manual del período. El rango incluye el pasado a propósito —
   * el caso normal es prorrogar un vencimiento que ya venció, que la vista oculta hasta que
   * la prórroga lo devuelve a la planilla.
   */
  async listarVencimientosCalendario(
    client: SupabaseClient<Database>,
    impuestoId: string,
  ): Promise<VencimientoCalendarioOpcion[]> {
    const desde = new Date()
    desde.setFullYear(desde.getFullYear() - 1)

    const { data, error } = await client
      .from('vencimientos_afip_calendario')
      .select('id, periodo, fecha_vencimiento, terminacion_cuit')
      .eq('impuesto_id', impuestoId)
      .eq('activo', true)
      // Mismo criterio que vista_calendario_fiscal: `source IS DISTINCT FROM 'ARCA'`.
      // `.neq()` solo no alcanza — descartaría también las filas con source NULL.
      .or('source.is.null,source.neq.ARCA')
      .gte('fecha_vencimiento', desde.toISOString().slice(0, 10))
      .order('fecha_vencimiento', { ascending: false })

    if (error) throw error
    return (data ?? []) as VencimientoCalendarioOpcion[]
  },

  /**
   * Crear prórroga
   */
  async crearProrroga(client: SupabaseClient<Database>, prorroga: ProrrogaInsert): Promise<Prorroga> {
    const { data, error } = await client
      .from('prorrogas_fiscales')
      .insert(prorroga)
      .select()
      .single()

    if (error) throw error
    return data as Prorroga
  },

  /**
   * Eliminar prórroga (soft delete)
   */
  async eliminarProrroga(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client
      .from('prorrogas_fiscales')
      .update({ activo: false, updated_at: new Date().toISOString() })
      .eq('id', id)

    if (error) throw error
  },

  /**
   * Generar obligaciones recurrentes (Type B) para un período
   */
  async generarObligacionesRecurrentes(
    client: SupabaseClient<Database>,
    periodo: string,
    organizacionId: string
  ): Promise<ResultadoGeneracionVencimientos> {
    const { data, error } = await client.rpc('generar_obligaciones_recurrentes' as never, {
      p_periodo: periodo,
      p_organizacion_id: organizacionId,
    } as never)

    if (error) throw error
    const resultado = Array.isArray(data) ? data[0] : data
    return resultado as ResultadoGeneracionVencimientos
  },

  /**
   * Generar vencimientos de Balances para un año fiscal
   * Calcula fecha per-client: cierre_ejercicio + 4 meses, último día del mes
   */
  async generarVencimientosBalances(
    client: SupabaseClient<Database>,
    organizacionId: string,
    year: number
  ): Promise<{ clientes_procesados: number; vencimientos_creados: number; clientes_sin_cierre: number }> {
    const { data, error } = await client.rpc('generar_vencimientos_balances' as never, {
      p_organizacion_id: organizacionId,
      p_year: year,
    } as never)

    if (error) throw error
    const resultado = Array.isArray(data) ? data[0] : data
    return resultado as { clientes_procesados: number; vencimientos_creados: number; clientes_sin_cierre: number }
  },
}
